import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, 
  FiEdit2, 
  FiSearch,
  FiX,
  FiCheck,
  FiPackage,
  FiEye,
  FiDollarSign,
  FiHash,
  FiGrid,
  FiList,
  FiAlertCircle,
  FiShoppingBag,
  FiGift,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiFilter,
  FiTrendingUp,
  FiClock,
  FiCalendar,
  FiTag,
  FiBox,
  FiChevronRight,
  FiChevronLeft,
  FiCheckCircle,
  FiMinusCircle,
  FiPlusCircle,
  FiToggleLeft,
  FiToggleRight,
  FiSettings,
  FiSmartphone,
  FiTablet,
  FiMonitor,
  FiArchive,
  FiLayers,
  FiCpu,
  FiPenTool,
  FiSave,
  FiRefreshCw,
  FiTrash2,
  FiLock,
  FiUnlock,
  FiAlertTriangle,
  FiShoppingCart,
  FiStar,
  FiZap,
  FiHeart,
  FiAward,
  FiDownload
} from 'react-icons/fi';
import { customProductItemAPI, productAPI, basedProductItemAPI, accountAPI } from '../utils/api';
import toast from 'react-hot-toast';
import useResponsive from '../hooks/useResponsive';

const CustomProductItems = ({ setCurrentPage }) => {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [basedItems, setBasedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [modalType, setModalType] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('code');
  const [userRole, setUserRole] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [activeTab, setActiveTab] = useState('basic');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [showBulkPriceModal, setShowBulkPriceModal] = useState(false);
  const [showLimitSeriesPromptModal, setShowLimitSeriesPromptModal] = useState(false);
  const [showLimitSeriesModal, setShowLimitSeriesModal] = useState(false);
  const [showLimitSeriesResultModal, setShowLimitSeriesResultModal] = useState(false);
  const [bulkPriceText, setBulkPriceText] = useState('');
  const [bulkPriceLoading, setBulkPriceLoading] = useState(false);
  const [bulkPriceSubmitting, setBulkPriceSubmitting] = useState(false);
  const [limitSeriesSubmitting, setLimitSeriesSubmitting] = useState(false);
  const [pendingLimitSeriesItem, setPendingLimitSeriesItem] = useState(null);
  const [limitSeriesResult, setLimitSeriesResult] = useState(null);
  const bulkPriceTextareaRef = useRef(null);
  const [limitSeriesForm, setLimitSeriesForm] = useState({
    based_item_id: '',
    code: '',
    name: '',
    reseller_price: '',
    user_price: ''
  });
  
  const [selectedBasedItems, setSelectedBasedItems] = useState([]);
  const [updateSelectedBasedItems, setUpdateSelectedBasedItems] = useState([]);
  
  // Track which type of items are selected
  const [hasLimitedSelected, setHasLimitedSelected] = useState(false);
  const [hasUnlimitedSelected, setHasUnlimitedSelected] = useState(false);
  
  // Track usage counts for based items with limits
  const [basedItemUsage, setBasedItemUsage] = useState({});
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    coin_price: '',
    reseller_price: '',
    user_price: '',
    based_item_ids: []
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActive, setIsActive] = useState('1');
  const [shakingTab, setShakingTab] = useState('');

  const { isMobile, isTablet, isDesktop } = useResponsive();

  // New color theme - Pink and Orange combination for tabs
  const theme = {
    primary: 'from-pink-600 to-rose-600',
    primaryLight: 'from-pink-500 to-rose-500',
    secondary: 'from-pink-500 to-orange-500',
    bg: 'bg-pink-100 dark:bg-pink-900/30',
    text: 'text-pink-600 dark:text-pink-400',
    border: 'border-pink-200 dark:border-pink-800',
    ring: 'ring-pink-500',
    hover: 'hover:bg-pink-50 dark:hover:bg-pink-900/20',
    gradient: 'from-pink-600 to-rose-600',
    lightBg: 'bg-pink-50 dark:bg-pink-900/20',
    tabGradient: 'from-orange-500 to-pink-500', // New tab gradient
    badge: {
      success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      info: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400'
    }
  };

  // Tab styling - New design with orange-pink gradient
  const tabStyles = {
    base: "relative flex-1 flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-medium transition-all duration-300 rounded-xl overflow-hidden",
    active: "bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg shadow-orange-500/30",
    inactive: "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 border-2 border-gray-100 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-800",
    icon: "w-4 h-4",
    activeIcon: "animate-pulse"
  };

  const naturalCollator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base'
  });

  const naturalCompare = (left, right) => naturalCollator.compare(left || '', right || '');

  const sortByCodeThenName = (list = []) => (
    [...list].sort((a, b) => {
      const codeComparison = naturalCompare(a.code, b.code);
      if (codeComparison !== 0) return codeComparison;

      const nameComparison = naturalCompare(a.name, b.name);
      if (nameComparison !== 0) return nameComparison;

      return (a.id || 0) - (b.id || 0);
    })
  );

  const sortProductsById = (list = []) => (
    [...list].sort((a, b) => (a.id || 0) - (b.id || 0))
  );

  const getSuggestedLimitSeriesCode = (item) => {
    const rawCode = String(item?.code || '').trim().toLowerCase();
    const cleanedCode = rawCode.replace(/\d+$/g, '');
    return cleanedCode || rawCode;
  };

  const getSuggestedLimitSeriesName = (item) => {
    const rawName = String(item?.name || '').trim();
    return rawName.replace(/\s+\d+$/g, '') || rawName;
  };

  const getSuggestedLimitSeriesPrices = (item) => {
    const fallbackResellerPrice = formData?.reseller_price !== undefined && formData?.reseller_price !== null && formData?.reseller_price !== ''
      ? String(formatPrice(formData.reseller_price))
      : '';
    const fallbackUserPrice = formData?.user_price !== undefined && formData?.user_price !== null && formData?.user_price !== ''
      ? String(formatPrice(formData.user_price))
      : '';

    if (!item) {
      return { reseller_price: fallbackResellerPrice, user_price: fallbackUserPrice };
    }

    return {
      reseller_price: item.reseller_price !== undefined && item.reseller_price !== null && item.reseller_price !== ''
        ? String(formatPrice(item.reseller_price))
        : fallbackResellerPrice,
      user_price: item.user_price !== undefined && item.user_price !== null && item.user_price !== ''
        ? String(formatPrice(item.user_price))
        : fallbackUserPrice
    };
  };

  const getLimitSeriesCodeError = (code) => {
    const trimmedCode = String(code || '').trim();
    if (!trimmedCode) return '';
    if (/\d$/.test(trimmedCode)) {
      return 'Base code should not end with a number. Try values like wp, pass, or weekly_pack.';
    }
    return '';
  };

  const buildLimitSeriesPreview = (item, form) => {
    if (!item) return [];

    const limitCount = parseInt(item.limit_count, 10) || 0;
    const baseCode = String(form.code || '').trim();
    const baseName = String(form.name || '').trim();

    if (!baseCode || !baseName || limitCount < 1 || getLimitSeriesCodeError(baseCode)) {
      return [];
    }

    const resellerUnit = Math.max(0, parseInt(form.reseller_price, 10) || 0);
    const userUnit = Math.max(0, parseInt(form.user_price, 10) || 0);

    return Array.from({ length: limitCount + 1 }, (_, index) => {
      const priceMultiplier = index <= 1 ? 1 : index;

      return {
        index,
        code: index === 0 ? baseCode : `${baseCode}${index}`,
        name: index === 0 ? baseName : `${baseName}${index}`,
        reseller_price: resellerUnit * priceMultiplier,
        user_price: userUnit * priceMultiplier
      };
    });
  };

  useEffect(() => {
    fetchUserAndData();
  }, []);

  useEffect(() => {
    if (selectedProductId) {
      resetBulkPriceTools();
      resetLimitSeriesTools();
      fetchItems(selectedProductId);
      checkProductAndLoadBasedItems(selectedProductId);
    } else {
      setItems([]);
      setBasedItems([]);
      resetBulkPriceTools();
      resetLimitSeriesTools();
    }
  }, [selectedProductId]);

  // Update selection type when selected items change
  useEffect(() => {
    checkSelectionType();
  }, [selectedBasedItems, updateSelectedBasedItems, modalType]);

  useEffect(() => {
    const eligibleItemIds = new Set(
      basedItems
        .filter(item => item.has_limit && (parseInt(item.limit_count, 10) || 0) > 0)
        .map(item => String(item.id))
    );

    setLimitSeriesForm(prev => {
      if (!prev.based_item_id || eligibleItemIds.has(String(prev.based_item_id))) {
        return prev;
      }

      return {
        ...prev,
        based_item_id: ''
      };
    });
  }, [basedItems]);

  useEffect(() => {
    if (!(showModal || showBulkPriceModal || showLimitSeriesModal || showLimitSeriesPromptModal || showLimitSeriesResultModal || showDeleteModal)) {
      return undefined;
    }

    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyTouchAction = document.body.style.touchAction;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.touchAction = originalBodyTouchAction;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [showModal, showBulkPriceModal, showLimitSeriesModal, showLimitSeriesPromptModal, showLimitSeriesResultModal, showDeleteModal]);

  useEffect(() => {
    if (!showBulkPriceModal || !bulkPriceTextareaRef.current) return;

    const textarea = bulkPriceTextareaRef.current;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [showBulkPriceModal, bulkPriceText]);

  const checkSelectionType = () => {
    const currentSelection = modalType === 'edit' ? updateSelectedBasedItems : selectedBasedItems;
    
    // Check if any selected item has limit
    let hasLimit = false;
    let hasNoLimit = false;
    
    currentSelection.forEach(id => {
      const item = basedItems.find(i => i.id === id);
      if (item) {
        if (item.has_limit) {
          hasLimit = true;
        } else {
          hasNoLimit = true;
        }
      }
    });
    
    setHasLimitedSelected(hasLimit);
    setHasUnlimitedSelected(hasNoLimit);
  };

  const fetchUserAndData = async () => {
    try {
      setLoading(true);
      const userResponse = await accountAPI.getMe();
      if (userResponse.data.success) {
        const user = userResponse.data.data;
        setCurrentUser(user);
        setUserRole(user.role);
      }

      const productsResponse = await productAPI.list();
      if (productsResponse.data && productsResponse.data.success) {
        let allProducts = productsResponse.data.data || [];
        
        if (userRole === 'super_admin' && currentUser) {
          allProducts = allProducts.filter(p => p.admin_id === currentUser?.id);
        }
        
        setProducts(sortProductsById(allProducts));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async (productId) => {
    if (!productId) return;
    
    setItemsLoading(true);
    try {
      const response = await customProductItemAPI.list(productId);
      if (response.data && response.data.success) {
        const items = (response.data.data || []).map(item => ({
          ...item,
          product: selectedProduct
        }));
        setItems(items);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  };

  const getCurrentSelection = () => (
    modalType === 'edit' ? updateSelectedBasedItems : selectedBasedItems
  );

  const getOriginalEditBasedItemIds = () => (
    modalType === 'edit' && Array.isArray(formData.based_item_ids)
      ? formData.based_item_ids
      : []
  );

  const getSelectionFlags = (selection = getCurrentSelection()) => {
    let hasLimited = false;
    let hasUnlimited = false;

    selection.forEach(id => {
      const item = basedItems.find(basedItem => basedItem.id === id);
      if (!item) return;

      if (item.has_limit) {
        hasLimited = true;
      } else {
        hasUnlimited = true;
      }
    });

    return { hasLimited, hasUnlimited };
  };

  const getSelectedLimitedItemId = (selection = getCurrentSelection()) => {
    const limitedItemId = selection.find(id => {
      const item = basedItems.find(basedItem => basedItem.id === id);
      return item?.has_limit;
    });

    return limitedItemId || null;
  };

  const getAdjustedUsageCount = (itemId) => {
    const usageCount = basedItemUsage[itemId] || 0;
    const originalEditCount = getOriginalEditBasedItemIds().filter(id => id === itemId).length;

    return Math.max(0, usageCount - originalEditCount);
  };

  const getRemainingLimitForItem = (item, selection = getCurrentSelection()) => {
    if (!item?.has_limit) return null;

    const limitCount = parseInt(item.limit_count, 10) || 0;
    const selectionCount = selection.filter(id => id === item.id).length;
    const adjustedUsageCount = getAdjustedUsageCount(item.id);

    return Math.max(0, limitCount - adjustedUsageCount - selectionCount);
  };

  const loadBasedItemUsage = async (productId = selectedProductId, itemsList = basedItems) => {
    if (!productId) return;

    try {
      const response = await customProductItemAPI.list(productId);
      if (response.data && response.data.success) {
        const customItems = response.data.data || [];
        const usage = {};
        
        // Initialize all based items with 0 usage
        itemsList.forEach(item => {
          usage[item.id] = 0;
        });
        
        // Count usage from existing custom items
        customItems.forEach(customItem => {
          if (customItem.based_item_ids) {
            customItem.based_item_ids.forEach(id => {
              if (usage[id] !== undefined) {
                usage[id] = (usage[id] || 0) + 1;
              }
            });
          }
        });
        
        setBasedItemUsage(usage);
      }
    } catch (error) {
      console.error('Error loading based item usage:', error);
    }
  };

  const checkProductAndLoadBasedItems = async (productId) => {
    const product = getProductDetails(productId);
    if (product && !isProductStockBased(productId)) {
      await loadBasedItems(productId);
    } else {
      setBasedItems([]);
      setBasedItemUsage({});
    }
  };

  const loadBasedItems = async (productId) => {
    try {
      const response = await basedProductItemAPI.list(productId);
      if (response.data && response.data.success) {
        const items = sortByCodeThenName((response.data.data || []).map(item => ({
          ...item,
          coin_price: normalizeMoney(item.coin_price),
          has_limit: isTruthyFlag(item.has_limit),
          limit_count: item.limit_count || 0
        })));
        setBasedItems(items);
        await loadBasedItemUsage(productId, items);
      }
    } catch (error) {
      console.error('Error loading based items:', error);
    }
  };

  const canCreate = () => ['system', 'super_admin'].includes(userRole);
  const canEdit = () => ['system', 'super_admin'].includes(userRole);
  const canDelete = () => ['system', 'super_admin'].includes(userRole);
  const canView = () => true;

  const isProductStockBased = (productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    return isTruthyFlag(product?.is_stock_based);
  };

  const getProductDetails = (productId) => {
    return products.find(p => p.id === parseInt(productId));
  };

  const normalizeMoney = (val) => {
    if (val === null || val === undefined) return '0';
    const s = String(val).trim();
    return s === '' ? '0' : s;
  };

  const isTruthyFlag = (value) => (
    value === 1 ||
    value === true ||
    value === '1' ||
    value === 'true'
  );

  const toFloat = (val) => {
    return parseFloat(normalizeMoney(val)) || 0;
  };

  const formatCoin = (val) => {
    const num = toFloat(val);
    return num.toFixed(2);
  };

  const formatPrice = (val) => {
    const num = toFloat(val);
    return Math.floor(num);
  };

  const calculateCoinSum = (selectionArray) => {
    let total = 0;
    selectionArray.forEach(id => {
      const item = basedItems.find(i => i.id === id);
      if (item) {
        total += toFloat(item.coin_price);
      }
    });
    return total.toFixed(2);
  };

  const resetBulkPriceTools = () => {
    setBulkPriceText('');
    setBulkPriceLoading(false);
    setBulkPriceSubmitting(false);
    setShowBulkPriceModal(false);
  };

  const buildBulkPriceTemplateFromItems = (sourceItems = items) => {
    if (!Array.isArray(sourceItems) || sourceItems.length === 0) {
      return '';
    }

    return sortByCodeThenName(sourceItems)
      .map(item => {
        const code = String(item?.code || '').trim();
        if (!code) return '';

        const resellerPrice = formatPrice(item?.reseller_price);
        const userPrice = formatPrice(item?.user_price);
        return `${code},${resellerPrice},${userPrice}`;
      })
      .filter(Boolean)
      .join('\n');
  };

  const resetLimitSeriesTools = () => {
    setLimitSeriesForm({
      based_item_id: '',
      code: '',
      name: '',
      reseller_price: '',
      user_price: ''
    });
    setLimitSeriesSubmitting(false);
    setPendingLimitSeriesItem(null);
    setShowLimitSeriesPromptModal(false);
    setShowLimitSeriesModal(false);
  };

  const closeLimitSeriesResultModal = () => {
    setShowLimitSeriesResultModal(false);
    setLimitSeriesResult(null);
  };

  const triggerTabShake = (tabId) => {
    setShakingTab(tabId);
    window.setTimeout(() => {
      setShakingTab(current => (current === tabId ? '' : current));
    }, 420);
  };

  const handleFetchBulkPriceTemplate = async () => {
    if (!selectedProductId) {
      toast.error('Please select a product first');
      return;
    }

    setBulkPriceLoading(true);
    try {
      const response = await customProductItemAPI.getBulkUpdateTemplate(selectedProductId);
      const apiBulkText = response.data.data?.bulk_text || '';
      const fallbackBulkText = buildBulkPriceTemplateFromItems();

      if (response.data?.success) {
        setBulkPriceText(apiBulkText || fallbackBulkText);
        setShowBulkPriceModal(true);
        toast.success('Bulk price template loaded');
      } else if (fallbackBulkText) {
        setBulkPriceText(fallbackBulkText);
        setShowBulkPriceModal(true);
        toast.success('Bulk price template loaded');
      } else {
        toast.error(response.data?.message || 'Failed to load bulk price template');
      }
    } catch (error) {
      console.error('Error loading bulk price template:', error);
      const fallbackBulkText = buildBulkPriceTemplateFromItems();
      if (fallbackBulkText) {
        setBulkPriceText(fallbackBulkText);
        setShowBulkPriceModal(true);
        toast.success('Bulk price template loaded');
      } else {
        toast.error(error.response?.data?.message || 'Failed to load bulk price template');
      }
    } finally {
      setBulkPriceLoading(false);
    }
  };

  const openBulkPriceModal = async () => {
    if (!selectedProductId) {
      toast.error('Please select a product first');
      return;
    }

    setShowBulkPriceModal(true);
    if (!bulkPriceText.trim()) {
      await handleFetchBulkPriceTemplate();
    }
  };

  const openLimitSeriesModalForItem = (item) => {
    const suggestedPrices = getSuggestedLimitSeriesPrices(item);

    setPendingLimitSeriesItem(item);
    setLimitSeriesForm({
      based_item_id: String(item?.id || ''),
      code: getSuggestedLimitSeriesCode(item),
      name: getSuggestedLimitSeriesName(item),
      reseller_price: suggestedPrices.reseller_price,
      user_price: suggestedPrices.user_price
    });
    setShowLimitSeriesPromptModal(false);
    setShowModal(false);
    setShowLimitSeriesModal(true);
  };

  const handleSubmitBulkPriceUpdate = async () => {
    if (!selectedProductId) {
      toast.error('Please select a product first');
      return;
    }

    if (!bulkPriceText.trim()) {
      toast.error('Bulk price text is empty');
      return;
    }

    setBulkPriceSubmitting(true);
    try {
      const response = await customProductItemAPI.bulkUpdatePrices({
        product_id: selectedProductId,
        bulk_text: bulkPriceText
      });

      if (response.data?.success) {
        toast.success(response.data?.message || 'Bulk prices updated successfully');
        await fetchItems(selectedProductId);
        resetBulkPriceTools();
      } else {
        toast.error(response.data?.message || 'Failed to update prices');
      }
    } catch (error) {
      console.error('Error updating prices in bulk:', error);
      toast.error(error.response?.data?.message || 'Failed to update prices');
    } finally {
      setBulkPriceSubmitting(false);
    }
  };

  const handleLimitSeriesCreate = async () => {
    if (!selectedProductId) {
      toast.error('Please select a product first');
      return;
    }

    const selectedBasedItem = basedItems.find(item => String(item.id) === String(limitSeriesForm.based_item_id));
    if (!selectedBasedItem) {
      toast.error('Please choose a limited based item');
      return;
    }

    const limitSeriesCodeError = getLimitSeriesCodeError(limitSeriesForm.code);
    if (!limitSeriesForm.code.trim() || limitSeriesCodeError) {
      toast.error(limitSeriesCodeError || 'Base code is required');
      return;
    }

    if (!limitSeriesForm.name.trim()) {
      toast.error('Base name is required');
      return;
    }

    if (limitSeriesForm.reseller_price === '' || limitSeriesForm.user_price === '') {
      toast.error('Please fill both reseller and user prices');
      return;
    }

    setLimitSeriesSubmitting(true);
    try {
      const response = await customProductItemAPI.createLimitSeries({
        product_id: selectedProductId,
        based_item_id: limitSeriesForm.based_item_id,
        code: limitSeriesForm.code.trim(),
        name: limitSeriesForm.name.trim(),
        reseller_price: limitSeriesForm.reseller_price,
        user_price: limitSeriesForm.user_price
      });

      if (response.data?.success) {
        toast.success(response.data?.message || 'Limit series created successfully');
        setLimitSeriesResult(response.data?.data || null);
        await Promise.all([
          fetchItems(selectedProductId),
          loadBasedItems(selectedProductId)
        ]);
        resetLimitSeriesTools();
        setShowLimitSeriesResultModal(true);
      } else {
        toast.error(response.data?.message || 'Failed to create limit series');
      }
    } catch (error) {
      console.error('Error creating limit series:', error);
      toast.error(error.response?.data?.message || 'Failed to create limit series');
    } finally {
      setLimitSeriesSubmitting(false);
    }
  };

  // Check if item can be added based on selection rules
  const canAddItem = (item) => {
    const currentSelection = getCurrentSelection();
    const { hasLimited, hasUnlimited } = getSelectionFlags(currentSelection);
    
    // If trying to add a limited item
    if (item.has_limit) {
      // Cannot add if we already have unlimited items selected
      if (hasUnlimited) return false;

      const selectedLimitedItemId = getSelectedLimitedItemId(currentSelection);
      if (selectedLimitedItemId && selectedLimitedItemId !== item.id) return false;

      const remainingLimit = getRemainingLimitForItem(item, currentSelection);

      return remainingLimit !== null && remainingLimit > 0;
    } 
    // If trying to add an unlimited item
    else {
      // Cannot add if we already have limited items selected
      if (hasLimited) return false;
      return true; // Unlimited items can be added any number of times
    }
  };

  const addBasedItem = (itemId) => {
    const item = basedItems.find(i => i.id === itemId);
    if (!item) return;
    
    if (!canAddItem(item)) return;

    const currentSelectionCount = selectedBasedItems.filter(id => id === itemId).length;
    if (item.has_limit && currentSelectionCount >= 1) {
      setPendingLimitSeriesItem(item);
      setShowLimitSeriesPromptModal(true);
    }

    setSelectedBasedItems(prev => [...prev, itemId]);
  };

  const removeBasedItem = (itemId) => {
    const index = selectedBasedItems.lastIndexOf(itemId);
    if (index > -1) {
      setSelectedBasedItems(prev => {
        const newArray = [...prev];
        newArray.splice(index, 1);
        return newArray;
      });
    }
  };

  const addUpdateBasedItem = (itemId) => {
    const item = basedItems.find(i => i.id === itemId);
    if (!item) return;
    
    if (!canAddItem(item)) return;

    const currentSelectionCount = updateSelectedBasedItems.filter(id => id === itemId).length;
    if (item.has_limit && currentSelectionCount >= 1) {
      setPendingLimitSeriesItem(item);
      setShowLimitSeriesPromptModal(true);
    }

    setUpdateSelectedBasedItems(prev => [...prev, itemId]);
  };

  const removeUpdateBasedItem = (itemId) => {
    const index = updateSelectedBasedItems.lastIndexOf(itemId);
    if (index > -1) {
      setUpdateSelectedBasedItems(prev => {
        const newArray = [...prev];
        newArray.splice(index, 1);
        return newArray;
      });
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value;
    const allowedValue = value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    setFormData({ ...formData, code: allowedValue });
    if (formErrors.code) {
      setFormErrors({ ...formErrors, code: null });
    }
  };

  const validateTab = (tab) => {
    const errors = {};
    
    if (tab === 'basic') {
      if (!formData.code) {
        errors.code = 'Code is required';
      } else if (formData.code.length < 2) {
        errors.code = 'Code must be at least 2 characters';
      }
      
      if (!formData.name) {
        errors.name = 'Name is required';
      } else if (formData.name.length < 2) {
        errors.name = 'Name must be at least 2 characters';
      }
    }
    
    if (tab === 'items') {
      const product = getProductDetails(selectedProductId);
      const isStockBased = product?.is_stock_based === 1;
      
      if (!isStockBased) {
        const ids = modalType === 'edit' ? updateSelectedBasedItems : selectedBasedItems;
        if (ids.length === 0) {
          errors.based_items = 'Please select at least one based item';
        }
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTabChange = (tab) => {
    if (tab === 'basic' || validateTab(activeTab)) {
      setActiveTab(tab);
    } else {
      triggerTabShake(activeTab);
    }
  };

  const handleCreate = async () => {
    if (!canCreate()) {
      toast.error('You do not have permission to create items');
      return;
    }

    if (!validateTab('items')) {
      setActiveTab('items');
      triggerTabShake('items');
      return;
    }

    setIsSubmitting(true);

    const product = getProductDetails(selectedProductId);
    const isStockBased = product?.is_stock_based === 1;

    const payload = {
      product_id: parseInt(selectedProductId),
      code: formData.code,
      name: formData.name,
      is_active: 1
    };

    if (formData.reseller_price && formData.reseller_price !== '') {
      payload.reseller_price = parseFloat(formData.reseller_price);
    }
    if (formData.user_price && formData.user_price !== '') {
      payload.user_price = parseFloat(formData.user_price);
    }

    if (!isStockBased) {
      payload.based_item_ids = selectedBasedItems;
      payload.coin_price = calculateCoinSum(selectedBasedItems);
    }

    try {
      const response = await customProductItemAPI.create(payload);
      if (response.data && response.data.success) {
        toast.success('Package created successfully');
        setShowModal(false);
        resetForm();
        fetchItems(selectedProductId);
        loadBasedItemUsage();
      } else {
        toast.error(response.data?.message || 'Failed to create item');
      }
    } catch (error) {
      console.error('Error creating item:', error);
      toast.error(error.response?.data?.message || 'Failed to create item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!canEdit()) {
      toast.error('You do not have permission to update items');
      return;
    }

    if (!selectedItem) {
      toast.error('No item selected');
      return;
    }

    if (!validateTab('items')) {
      setActiveTab('items');
      triggerTabShake('items');
      return;
    }

    setIsSubmitting(true);

    const product = getProductDetails(selectedProductId);
    const isStockBased = product?.is_stock_based === 1;

    const payload = {};

    if (formData.code && formData.code !== selectedItem.code) {
      payload.code = formData.code;
    }
    if (formData.name && formData.name !== selectedItem.name) {
      payload.name = formData.name;
    }
    if (formData.reseller_price && formData.reseller_price !== '' && 
        parseFloat(formData.reseller_price) !== selectedItem.reseller_price) {
      payload.reseller_price = parseFloat(formData.reseller_price);
    }
    if (formData.user_price && formData.user_price !== '' && 
        parseFloat(formData.user_price) !== selectedItem.user_price) {
      payload.user_price = parseFloat(formData.user_price);
    }

    if (isActive !== String(selectedItem.is_active)) {
      payload.is_active = parseInt(isActive);
    }

    if (!isStockBased) {
      payload.based_item_ids = updateSelectedBasedItems;
      payload.coin_price = calculateCoinSum(updateSelectedBasedItems);
    }

    if (Object.keys(payload).length === 0) {
      toast.error('No changes to update');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await customProductItemAPI.update(selectedItem.id, payload);
      if (response.data && response.data.success) {
        toast.success('Package updated successfully');
        setShowModal(false);
        resetForm();
        fetchItems(selectedProductId);
        loadBasedItemUsage();
      } else {
        toast.error(response.data?.message || 'Failed to update item');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error(error.response?.data?.message || 'Failed to update item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = async (item) => {
    if (!canEdit()) {
      toast.error('You do not have permission to edit items');
      return;
    }

    try {
      const response = await customProductItemAPI.get(item.id);
      if (response.data && response.data.success) {
        const fullItem = response.data.data.custom_product_item;
        const basedItemIds = response.data.data.based_item_ids || [];

        setSelectedItem(fullItem);
        setIsActive(String(fullItem.is_active));
        
        setFormData({
          code: fullItem.code || '',
          name: fullItem.name || '',
          coin_price: fullItem.coin_price || '',
          reseller_price: fullItem.reseller_price || '',
          user_price: fullItem.user_price || '',
          based_item_ids: basedItemIds
        });

        setUpdateSelectedBasedItems(basedItemIds);
        
        await loadBasedItems(fullItem.product_id);
        
        setModalType('edit');
        setShowModal(true);
        setActiveTab('basic');
        setFormErrors({});
      }
    } catch (error) {
      console.error('Error loading item details:', error);
      toast.error('Failed to load item details');
    }
  };

  const handleDelete = async () => {
    if (!canDelete() || !itemToDelete) return;

    try {
      const response = await customProductItemAPI.delete(itemToDelete.id);
      if (response.data && response.data.success) {
        toast.success('Item deleted successfully');
        fetchItems(selectedProductId);
        loadBasedItemUsage();
      } else {
        toast.error(response.data?.message || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(error.response?.data?.message || 'Failed to delete item');
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const openCreateModal = () => {
    if (!canCreate()) {
      toast.error('You do not have permission to create items');
      return;
    }

    if (!selectedProductId) {
      toast.error('Please select a product first');
      return;
    }

    resetForm();
    setSelectedBasedItems([]);
    setIsActive('1');
    setModalType('create');
    setShowModal(true);
    setActiveTab('basic');
    setFormErrors({});
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      coin_price: '',
      reseller_price: '',
      user_price: '',
      based_item_ids: []
    });
    setSelectedItem(null);
    setSelectedBasedItems([]);
    setUpdateSelectedBasedItems([]);
    setHasLimitedSelected(false);
    setHasUnlimitedSelected(false);
    setIsActive('1');
    setFormErrors({});
  };

  const filteredProducts = sortProductsById(
    products.filter(product =>
      product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      product.code.toLowerCase().includes(productSearch.toLowerCase())
    )
  );

  const selectedProduct = getProductDetails(selectedProductId);
  const selectedLimitSeriesItem = basedItems.find(
    item => String(item.id) === String(limitSeriesForm.based_item_id)
  );
  const limitSeriesCodeError = getLimitSeriesCodeError(limitSeriesForm.code);
  const limitSeriesPreview = buildLimitSeriesPreview(selectedLimitSeriesItem, limitSeriesForm);

  const getSortedItems = () => {
    let filtered = items.filter(item => {
      const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.code?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || 
                           (statusFilter === 'active' && item.is_active === 1) ||
                           (statusFilter === 'inactive' && item.is_active === 0);
      
      return matchesSearch && matchesStatus;
    });

    switch(sortBy) {
      case 'code':
        return filtered.sort((a, b) => {
          const codeComparison = naturalCompare(a.code, b.code);
          if (codeComparison !== 0) return codeComparison;
          return naturalCompare(a.name, b.name);
        });
      case 'newest':
        return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'oldest':
        return filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'name':
        return filtered.sort((a, b) => {
          const nameComparison = naturalCompare(a.name, b.name);
          if (nameComparison !== 0) return nameComparison;
          return naturalCompare(a.code, b.code);
        });
      case 'price':
        return filtered.sort((a, b) => (parseFloat(b.coin_price) || 0) - (parseFloat(a.coin_price) || 0));
      default:
        return filtered;
    }
  };

  const displayedItems = getSortedItems();

  const totalItems = displayedItems.length;
  const activeItems = displayedItems.filter(i => i.is_active === 1).length;
  const totalCoinValue = displayedItems.reduce((sum, item) => sum + (parseFloat(item.coin_price) || 0), 0);
  const averageCoinValue = totalItems > 0 ? (totalCoinValue / totalItems).toFixed(2) : '0.00';

  const TabButton = ({ id, label, icon: Icon, isActive, onClick, error }) => (
    <button
      onClick={() => onClick(id)}
      type="button"
      className={`
        ${tabStyles.base}
        ${isActive ? tabStyles.active : tabStyles.inactive}
        ${shakingTab === id ? 'animate-[shake_0.38s_ease-in-out]' : ''}
        group
      `}
    >
      {isActive && (
        <motion.div
          layoutId="activeTab"
          className="absolute inset-0 bg-white/10"
          initial={false}
          transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
        />
      )}
      <Icon className={`${tabStyles.icon} ${isActive ? tabStyles.activeIcon : 'group-hover:scale-110 transition-transform'}`} />
      <span className="hidden sm:inline relative z-10">{label}</span>
      {error && (
        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full animate-pulse" />
      )}
    </button>
  );

  const inputShellBaseClasses = `
    flex items-center gap-3 rounded-xl border-2 bg-white dark:bg-gray-800
    px-3 py-3 transition-all duration-200
    focus-within:ring-2 focus-within:ring-pink-500 focus-within:border-transparent
  `;

  const getInputShellClasses = (hasError = false) => `
    ${inputShellBaseClasses}
    ${hasError
      ? 'border-red-300 focus-within:ring-red-500'
      : 'border-gray-200 dark:border-gray-700 hover:border-pink-300'
    }
  `;

  const inputElementClasses = `
    min-w-0 flex-1 bg-transparent text-sm text-gray-900 dark:text-white outline-none
    placeholder:text-gray-400
  `;

  const BasedItemCard = ({ item, isSelected = false, onAdd, onRemove, selectionCount = 0 }) => {
  const hasLimit = item.has_limit;
  const limitCount = parseInt(item.limit_count, 10) || 0;
  const currentSelection = getCurrentSelection();
  const { hasLimited, hasUnlimited } = getSelectionFlags(currentSelection);
  const selectedLimitedItemId = getSelectedLimitedItemId(currentSelection);
  
  // Calculate remaining limit for limited items
  let remainingLimit = hasLimit ? getRemainingLimitForItem(item, currentSelection) : null;
  let canAdd = true;
  let addButtonTitle = 'Add item';
  
  if (hasLimit) {
    if (hasUnlimited) {
      canAdd = false;
      addButtonTitle = 'Cannot mix limited and unlimited items';
    } else if (selectedLimitedItemId && selectedLimitedItemId !== item.id) {
      canAdd = false;
      addButtonTitle = 'You can only choose one limited base item';
    } else if (remainingLimit === 0) {
      canAdd = false;
      addButtonTitle = 'Limit reached';
    } else {
      addButtonTitle = `Add item (${selectionCount}/${limitCount} selected)`;
    }
  } else if (hasLimited) {
    canAdd = false;
    addButtonTitle = 'Remove the limited item before adding unlimited items';
  }

  return (
    <div
      className={`
        relative bg-white dark:bg-gray-800 rounded-xl overflow-hidden
        border-2 transition-all duration-300
        ${isSelected 
          ? 'border-pink-500 dark:border-pink-400 shadow-lg shadow-pink-500/20' 
          : !canAdd && !isSelected
          ? 'border-gray-200 dark:border-gray-700 opacity-50'
          : 'border-gray-200 dark:border-gray-700 hover:border-pink-300 dark:hover:border-pink-700'
        }
      `}
    >
      {/* Left accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${theme.primary}`} />
      
      <div className="p-4 pl-5">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="px-2.5 py-1 bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 rounded-lg text-xs font-medium">
                {item.code}
              </span>
              
              {hasLimit ? (
                <span className={`
                  px-2.5 py-1 rounded-lg text-xs font-medium flex items-center gap-1
                  ${remainingLimit === 0 
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                    : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                  }
                `}>
                  <FiLock className="w-3 h-3" />
                  {remainingLimit}/{limitCount} left
                </span>
              ) : (
                <span className="px-2.5 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-lg text-xs font-medium flex items-center gap-1">
                  <FiUnlock className="w-3 h-3" />
                  Unlimited
                </span>
              )}
            </div>
            
            {/* Name */}
            <h4 className="font-semibold text-gray-900 dark:text-white text-base mb-2 line-clamp-1">
              {item.name}
            </h4>
            
            {/* Coin price */}
            <div className="flex items-center gap-1.5">
              <FiDollarSign className="w-4 h-4 text-pink-500" />
              <span className="font-medium text-pink-600 dark:text-pink-400">
                {formatCoin(item.coin_price)} Coin
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col items-end gap-2">
            {isSelected ? (
              <>
                <div className="flex items-center gap-2">
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onRemove(item.id)}
                    className="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                    type="button"
                  >
                    <FiMinusCircle className="w-5 h-5" />
                  </button>
                  
                  <span className="min-w-[32px] text-center font-semibold text-pink-600 dark:text-pink-400">
                    {selectionCount}
                  </span>
                  
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onAdd(item.id)}
                    disabled={!canAdd}
                    className={`
                      p-2 rounded-xl transition-all
                      ${canAdd
                        ? `bg-gradient-to-r ${theme.primary} text-white hover:from-pink-700 hover:to-rose-700 shadow-md shadow-pink-500/20`
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      }
                    `}
                    type="button"
                    title={addButtonTitle}
                  >
                    <FiPlusCircle className="w-5 h-5" />
                  </button>
                </div>
                
                {hasLimit && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {selectionCount}/{limitCount} selected
                  </span>
                )}
              </>
            ) : (
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => canAdd && onAdd(item.id)}
                disabled={!canAdd}
                className={`
                  p-2.5 rounded-xl transition-all
                  ${canAdd
                    ? `bg-gradient-to-r ${theme.primary} text-white hover:from-pink-700 hover:to-rose-700 shadow-md shadow-pink-500/20`
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  }
                `}
                type="button"
                title={addButtonTitle}
              >
                <FiPlus className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

  const SelectedItemsList = ({ items: selectedIds, onRemove }) => {
  // Group and count selected items (only needed for unlimited items)
  const groupedItems = {};
  selectedIds.forEach(id => {
    if (!groupedItems[id]) {
      groupedItems[id] = 0;
    }
    groupedItems[id]++;
  });

  const selectedItemsList = Object.entries(groupedItems).map(([id, count]) => {
    const item = basedItems.find(i => i.id === parseInt(id));
    return { ...item, count };
  }).filter(item => item.id);

  if (selectedItemsList.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-24 h-24 bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 rounded-2xl flex items-center justify-center mb-4">
          <FiPackage className="w-12 h-12 text-pink-400 dark:text-pink-500" />
        </div>
        <p className="text-base font-medium text-gray-900 dark:text-white mb-2">
          No Items Selected
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-xs">
          Click the + button on available items to add them to your package
        </p>
      </div>
    );
  }

  const totalCoin = calculateCoinSum(selectedIds);

  return (
    <div className="space-y-4">
      {selectedItemsList.map(item => (
        <div
          key={item.id}
          className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 rounded-xl p-4 border-2 border-pink-100 dark:border-pink-800/50"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {item.has_limit ? (
                <span className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg flex items-center justify-center text-sm font-bold">
                  {item.count}
                </span>
              ) : (
                <span className="w-8 h-8 bg-gradient-to-br from-pink-600 to-rose-600 text-white rounded-lg flex items-center justify-center text-sm font-bold shadow-md">
                  {item.count}
                </span>
              )}
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {item.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {item.code}
                  {item.has_limit && ` • Limit: ${item.limit_count}`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-semibold text-pink-600 dark:text-pink-400">
                  {formatCoin(toFloat(item.coin_price) * item.count)} Coin
                </p>
                {item.count > 1 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatCoin(item.coin_price)} × {item.count}
                  </p>
                )}
              </div>
              
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onRemove(item.id)}
                className="p-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                title={item.has_limit ? 'Remove' : 'Remove one'}
                type="button"
              >
                <FiMinusCircle className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Selection Type Info */}
      {hasLimitedSelected && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 border border-amber-200 dark:border-amber-800">
          <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1">
            <FiInfo className="w-3 h-3" />
            Limited item selected. Only that item can be added again until its limit is reached.
          </p>
        </div>
      )}

      {hasUnlimitedSelected && (
        <div className="bg-teal-50 dark:bg-teal-900/20 rounded-xl p-3 border border-teal-200 dark:border-teal-800">
          <p className="text-xs text-teal-700 dark:text-teal-400 flex items-center gap-1">
            <FiInfo className="w-3 h-3" />
            Unlimited items selected. You cannot add limited items.
          </p>
        </div>
      )}

      {/* Total Summary */}
      <div className={`bg-gradient-to-r ${theme.gradient} rounded-xl p-5 mt-6 shadow-xl`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-pink-100 mb-1">Total Coin Value</p>
            <p className="text-3xl font-bold text-white">{totalCoin} Coin</p>
            <p className="text-xs text-pink-200 mt-2">
              {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected
            </p>
          </div>
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <FiDollarSign className="w-7 h-7 text-white" />
          </div>
        </div>
      </div>
    </div>
  );
};

  const ItemGridCard = ({ item }) => {
    const canEditThis = canEdit();
    
    return (
      <div
        className="group bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              item.is_active === 1
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}>
              {item.is_active === 1 ? 'Active' : 'Inactive'}
            </span>
            <span className="text-xs text-gray-400">ID: {item.id}</span>
          </div>

          <div className={`w-12 h-12 bg-gradient-to-br from-pink-100 to-rose-100 dark:from-pink-900/30 dark:to-rose-900/30 rounded-xl flex items-center justify-center mb-4`}>
            <FiPackage className={`w-6 h-6 ${theme.text}`} />
          </div>

          <h3 className="font-semibold text-gray-900 dark:text-white text-lg mb-1 line-clamp-1">
            {item.name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-1">
            <FiHash className="w-3 h-3" />
            {item.code}
          </p>

          <div className="flex flex-wrap gap-2 mt-auto">
            {(userRole === 'system' || userRole === 'super_admin') && (
              <span className={`px-3 py-1.5 bg-pink-50 dark:bg-pink-900/30 ${theme.text} rounded-lg text-xs font-medium flex items-center gap-1`}>
                <FiDollarSign className="w-3 h-3" />
                {formatCoin(item.coin_price)} Coin
              </span>
            )}
            
            {userRole === 'system' && (
              <>
                {item.reseller_price > 0 && (
                  <span className="px-3 py-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg text-xs font-medium flex items-center gap-1">
                    <FiTag className="w-3 h-3" />
                    Reseller: {formatPrice(item.reseller_price)} Ks
                  </span>
                )}
                {item.user_price > 0 && (
                  <span className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-xs font-medium flex items-center gap-1">
                    <FiTag className="w-3 h-3" />
                    User: {formatPrice(item.user_price)} Ks
                  </span>
                )}
              </>
            )}
            
            {userRole === 'super_admin' && (
              <>
                {item.reseller_price > 0 && (
                  <span className="px-3 py-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg text-xs font-medium flex items-center gap-1">
                    <FiTag className="w-3 h-3" />
                    Reseller: {formatPrice(item.reseller_price)} Ks
                  </span>
                )}
                {item.user_price > 0 && (
                  <span className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-xs font-medium flex items-center gap-1">
                    <FiTag className="w-3 h-3" />
                    User: {formatPrice(item.user_price)} Ks
                  </span>
                )}
              </>
            )}
            
            {userRole === 'reseller' && item.reseller_price > 0 && (
              <span className="px-3 py-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg text-xs font-medium flex items-center gap-1">
                <FiTag className="w-3 h-3" />
                Price: {formatPrice(item.reseller_price)} Ks
              </span>
            )}
            
            {userRole === 'user' && item.user_price > 0 && (
              <span className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-xs font-medium flex items-center gap-1">
                <FiTag className="w-3 h-3" />
                Price: {formatPrice(item.user_price)} Ks
              </span>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <FiCalendar className="w-3 h-3" />
              {new Date(item.created_at).toLocaleDateString()}
            </div>
            <div className="flex gap-1">
              {selectedProduct?.is_stock_based === 1 && (userRole === 'system' || userRole === 'super_admin') && (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    setCurrentPage('stock-management', {
                      productId: item.product_id,
                      customItemId: item.id
                    });
                  }}
                  className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                  title="Manage Stock"
                >
                  <FiArchive className="w-4 h-4" />
                </motion.button>
              )}
              {canEditThis && (
                <div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => openEditModal(item)}
                  className={`p-2 ${theme.hover} rounded-xl transition-colors ${theme.text}`}
                  title="Edit item"
                >
                  <FiEdit2 className="w-4 h-4" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ItemTableRow = ({ item }) => {
    const canEditThis = canEdit();
    
    return (
      <motion.tr
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {item.id}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {item.code}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {item.name}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            item.is_active === 1
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
          }`}>
            {item.is_active === 1 ? 'Active' : 'Inactive'}
          </span>
        </td>
        
        {(userRole === 'system' || userRole === 'super_admin') && (
          <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
            {formatCoin(item.coin_price)} Coin
          </td>
        )}
        
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">
          {userRole === 'system' && (
            <div className="space-y-1">
              {item.reseller_price > 0 && (
                <div className="text-amber-600 dark:text-amber-400">R: {formatPrice(item.reseller_price)} Ks</div>
              )}
              {item.user_price > 0 && (
                <div className="text-blue-600 dark:text-blue-400">U: {formatPrice(item.user_price)} Ks</div>
              )}
            </div>
          )}
          {userRole === 'super_admin' && (
            <div className="space-y-1">
              {item.reseller_price > 0 && (
                <div className="text-amber-600 dark:text-amber-400">R: {formatPrice(item.reseller_price)} Ks</div>
              )}
              {item.user_price > 0 && (
                <div className="text-blue-600 dark:text-blue-400">U: {formatPrice(item.user_price)} Ks</div>
              )}
            </div>
          )}
          {userRole === 'reseller' && item.reseller_price > 0 && (
            <div className="text-amber-600 dark:text-amber-400">{formatPrice(item.reseller_price)} Ks</div>
          )}
          {userRole === 'user' && item.user_price > 0 && (
            <div className="text-blue-600 dark:text-blue-400">{formatPrice(item.user_price)} Ks</div>
          )}
        </td>
        
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <FiCalendar className="w-3 h-3" />
            {new Date(item.created_at).toLocaleDateString()}
          </div>
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">
          <div className="flex items-center gap-2">
            {selectedProduct?.is_stock_based === 1 && (userRole === 'system' || userRole === 'super_admin') && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  setCurrentPage('stock-management', {
                    productId: item.product_id,
                    customItemId: item.id
                  });
                }}
                className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                title="Manage Stock"
              >
                <FiArchive className="w-4 h-4" />
              </motion.button>
            )}
            {canEditThis && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => openEditModal(item)}
                className={`p-2 ${theme.hover} rounded-lg transition-colors ${theme.text}`}
                title="Edit"
              >
                <FiEdit2 className="w-4 h-4" />
              </motion.button>
            )}
          </div>
        </td>
      </motion.tr>
    );
  };

  if (!canView()) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
            <FiEye className="w-10 h-10 text-red-500 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Access Denied</h3>
          <p className="text-gray-500 dark:text-gray-400">You do not have permission to view this page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-r ${theme.gradient} rounded-2xl p-6 sm:p-8 shadow-lg`}
      >
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
          >
            <FiGift className="w-7 h-7 text-white" />
          </motion.div>
          <div>
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl md:text-3xl font-bold text-white"
            >
              Packages
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-pink-100 text-sm mt-1"
            >
              Create and manage packages with smart item selection
            </motion.p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700"
      >
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Select Product <span className="text-red-500">*</span>
        </label>
        
        <div className="relative">
          <button
            onClick={() => setShowProductDropdown(!showProductDropdown)}
            className="w-full px-4 py-3 pr-11 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent text-left flex items-center justify-between text-gray-900 dark:text-white hover:border-pink-300 transition-colors"
            type="button"
          >
            <span className="truncate flex items-center gap-2">
              {selectedProduct ? (
                <>
                  <FiPackage className={`w-4 h-4 ${theme.text}`} />
                  <span className="font-medium">{selectedProduct.name}</span>
                  <span className="text-gray-400">({selectedProduct.code})</span>
                </>
              ) : (
                <span className="text-gray-400">-- Choose a product --</span>
              )}
            </span>
            <motion.div
              animate={{ rotate: showProductDropdown ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="absolute right-4 top-1/2 -translate-y-1/2"
            >
              <FiChevronDown className="w-5 h-5 text-gray-400" />
            </motion.div>
          </button>

          <AnimatePresence>
            {showProductDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute z-10 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden"
              >
                <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                  <div className="relative">
                    <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 sm:pr-4"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>

                <div className="max-h-60 overflow-y-auto scrollbar-hide">
                  {filteredProducts.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No products found
                    </div>
                  ) : (
                    filteredProducts.map((product) => {
                      const isStockBased = product.is_stock_based === 1;
                      return (
                        <motion.button
                          key={product.id}
                          whileHover={{ backgroundColor: 'rgba(236, 72, 153, 0.05)' }}
                          onClick={() => {
                            setSelectedProductId(String(product.id));
                            setShowProductDropdown(false);
                            setProductSearch('');
                          }}
                          className="w-full px-4 py-3 text-left flex items-center justify-between border-b border-gray-100 dark:border-gray-700 last:border-0"
                          type="button"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              isStockBased
                                ? 'bg-amber-100 dark:bg-amber-900/30'
                                : 'bg-pink-100 dark:bg-pink-900/30'
                            }`}>
                              <FiPackage className={`w-4 h-4 ${
                                isStockBased
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-pink-600 dark:text-pink-400'
                              }`} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                {product.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Code: {product.code}
                              </p>
                            </div>
                          </div>
                          {selectedProductId === String(product.id) && (
                            <FiCheckCircle className={`w-5 h-5 ${theme.text}`} />
                          )}
                        </motion.button>
                      );
                    })
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {selectedProductId && selectedProduct && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4"
          >
            <div className={`flex items-center gap-3 p-4 rounded-xl ${
              isProductStockBased(selectedProductId)
                ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                : 'bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                isProductStockBased(selectedProductId) ? 'bg-amber-500' : 'bg-pink-500'
              }`} />
              <p className={`text-sm ${
                isProductStockBased(selectedProductId)
                  ? 'text-amber-700 dark:text-amber-400'
                  : 'text-pink-700 dark:text-pink-400'
              }`}>
                {isProductStockBased(selectedProductId)
                  ? 'This is a stock-based product. No based items needed.'
                  : 'This is a non-stock product. You need to select based items.'}
              </p>
            </div>
          </motion.div>
        )}
      </motion.div>

      {selectedProductId && (
        <>
          {/* Statistics Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {[
              { label: 'Total Items', value: totalItems, icon: FiPackage, color: 'pink' },
              { label: 'Active', value: activeItems, icon: FiTrendingUp, color: 'green' },
              { label: 'Inactive', value: totalItems - activeItems, icon: FiClock, color: 'amber' },
              ...(userRole === 'system' || userRole === 'super_admin' ? [{
                label: 'Avg Coin',
                value: `${averageCoinValue} Coin`,
                icon: FiDollarSign,
                color: 'pink'
              }] : [])
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-10 h-10 bg-${stat.color}-100 dark:bg-${stat.color}-900/30 rounded-lg flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Search and Actions Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 sm:left-3 sm:h-5 sm:w-5" />
                <input
                  type="text"
                  placeholder="Search by name or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400 sm:pr-4"
                />
              </div>

              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
                >
                  <option value="code">Code (0-Z)</option>
                  <option value="name">Name (0-Z)</option>
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="price">Price</option>
                </select>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-3 border rounded-xl flex items-center gap-2 transition-colors ${
                    showFilters 
                      ? `bg-gradient-to-r ${theme.primary} text-white border-pink-600` 
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-pink-300'
                  }`}
                >
                  <FiFilter className="w-5 h-5" />
                  <span className="hidden sm:inline">Filter</span>
                </button>

                {!isMobile && (
                  <div className="hidden sm:flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === 'table' 
                          ? `bg-white dark:bg-gray-600 ${theme.text} shadow-sm` 
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                      }`}
                      title="Table view"
                    >
                      <FiList className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === 'grid' 
                          ? `bg-white dark:bg-gray-600 ${theme.text} shadow-sm` 
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                      }`}
                      title="Grid view"
                    >
                      <FiGrid className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {canEdit() && selectedProductId && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={openBulkPriceModal}
                    className="px-4 py-3 bg-white dark:bg-gray-800 border border-pink-200 dark:border-pink-900/40 text-pink-600 dark:text-pink-300 rounded-xl hover:bg-pink-50 dark:hover:bg-pink-900/10 transition-all flex items-center gap-2 whitespace-nowrap"
                  >
                    <FiTag className="w-5 h-5" />
                    <span className="hidden sm:inline">Bulk Update Price</span>
                  </motion.button>
                )}

                {canCreate() && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={openCreateModal}
                    className={`px-4 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl hover:from-pink-700 hover:to-rose-700 transition-all shadow-lg shadow-pink-500/20 flex items-center gap-2 whitespace-nowrap`}
                  >
                    <FiPlus className="w-5 h-5" />
                    <span className="hidden sm:inline">Add New Item</span>
                  </motion.button>
                )}
              </div>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
                >
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
                    <div className="flex gap-2">
                      {['all', 'active', 'inactive'].map((status) => (
                        <motion.button
                          key={status}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setStatusFilter(status)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            statusFilter === status
                              ? status === 'active' ? 'bg-green-600 text-white'
                                : status === 'inactive' ? 'bg-red-600 text-white'
                                : `bg-gradient-to-r ${theme.primary} text-white`
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {itemsLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-pink-200 dark:border-pink-900 rounded-full"></div>
                <div className="w-16 h-16 border-4 border-pink-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
              </div>
            </div>
          ) : displayedItems.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700"
            >
              <div className={`w-24 h-24 mx-auto bg-gradient-to-br from-pink-100 to-rose-100 dark:from-pink-900/30 dark:to-rose-900/30 rounded-full flex items-center justify-center mb-4`}>
                <FiGift className={`w-12 h-12 ${theme.text}`} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Packages Found</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                {searchTerm || statusFilter !== 'all' || sortBy !== 'code'
                  ? 'No items match your filters.' 
                  : 'Get started by creating your first package.'}
              </p>
              {canCreate() && !searchTerm && statusFilter === 'all' && sortBy === 'code' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openCreateModal}
                  className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${theme.primary} text-white rounded-lg hover:from-pink-700 hover:to-rose-700 transition-colors text-sm shadow-lg shadow-pink-500/20`}
                >
                  <FiPlus className="mr-2 w-4 h-4" />
                  Add your first package
                </motion.button>
              )}
            </motion.div>
          ) : (
            <>
              {isMobile ? (
                <div className="grid grid-cols-1 gap-4">
                  <AnimatePresence>
                    {displayedItems.map((item) => (
                      <ItemGridCard key={item.id} item={item} />
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <AnimatePresence>
                        {displayedItems.map((item) => (
                          <ItemGridCard key={item.id} item={item} />
                        ))}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700">
                      <div className="overflow-x-auto scrollbar-hide">
                        <table className="w-full min-w-[900px]">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                              {(userRole === 'system' || userRole === 'super_admin') && (
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Coin Price</th>
                              )}
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Price</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            <AnimatePresence>
                              {displayedItems.map((item) => (
                                <ItemTableRow key={item.id} item={item} />
                              ))}
                            </AnimatePresence>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Modal Header */}
                <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-5`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200 }}
                        className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
                      >
                        {modalType === 'create' ? (
                          <FiPlus className="w-5 h-5 text-white" />
                        ) : (
                          <FiEdit2 className="w-5 h-5 text-white" />
                        )}
                      </motion.div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {modalType === 'create' ? 'Create Package' : 'Edit Package'}
                        </h3>
                        <p className="text-sm text-pink-100">
                          Product: {selectedProduct?.name}
                        </p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowModal(false)}
                      className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                      type="button"
                    >
                      <FiX className="w-5 h-5 text-white" />
                    </motion.button>
                  </div>

                  {/* Tab Navigation - New colors */}
                  <div className="flex gap-3 mt-6">
                    <TabButton
                      id="basic"
                      label="Basic Info"
                      icon={FiInfo}
                      isActive={activeTab === 'basic'}
                      onClick={handleTabChange}
                      error={formErrors.code || formErrors.name}
                    />
                    <TabButton
                      id="items"
                      label="Based Item"
                      icon={FiLayers}
                      isActive={activeTab === 'items'}
                      onClick={handleTabChange}
                      error={formErrors.based_items}
                    />
                    <TabButton
                      id="pricing"
                      label="Pricing"
                      icon={FiDollarSign}
                      isActive={activeTab === 'pricing'}
                      onClick={handleTabChange}
                    />
                  </div>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-y-auto scrollbar-hide p-6 bg-gray-50 dark:bg-gray-900">
                  <AnimatePresence mode="wait">
                    {/* Basic Info Tab */}
                    {activeTab === 'basic' && (
                      <motion.div
                        key="basic"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-5"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          {/* Code Field */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Code <span className="text-red-500">*</span>
                            </label>
                            <div className={getInputShellClasses(Boolean(formErrors.code))}>
                              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-pink-50 text-pink-500 dark:bg-pink-900/30 dark:text-pink-300">
                                <FiHash className="h-4 w-4" />
                              </span>
                              <input
                                type="text"
                                value={formData.code}
                                onChange={handleCodeChange}
                                className={inputElementClasses}
                                placeholder="e.g., c1, ab, item01"
                              />
                            </div>
                            {formErrors.code && (
                              <motion.p
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-2 text-xs text-red-500 flex items-center gap-1"
                              >
                                <FiAlertCircle className="w-3 h-3" />
                                {formErrors.code}
                              </motion.p>
                            )}
                            <p className="mt-2 text-xs text-gray-500">
                              Lowercase letters, numbers, hyphens, underscores only
                            </p>
                          </div>

                          {/* Name Field */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Name <span className="text-red-500">*</span>
                            </label>
                            <div className={getInputShellClasses(Boolean(formErrors.name))}>
                              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-pink-50 text-pink-500 dark:bg-pink-900/30 dark:text-pink-300">
                                <FiTag className="h-4 w-4" />
                              </span>
                              <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className={inputElementClasses}
                                placeholder="Enter item name"
                              />
                            </div>
                            {formErrors.name && (
                              <motion.p
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-2 text-xs text-red-500 flex items-center gap-1"
                              >
                                <FiAlertCircle className="w-3 h-3" />
                                {formErrors.name}
                              </motion.p>
                            )}
                          </div>
                        </div>

                        {/* Status Toggle - Edit Mode Only */}
                        {modalType === 'edit' && (
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="mt-6 pt-6 border-t-2 border-gray-100 dark:border-gray-700"
                          >
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                              Status
                            </label>
                            <div className="flex items-center gap-4">
                              <button
                                onClick={() => setIsActive('1')}
                                className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                                  isActive === '1'
                                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 shadow-md'
                                    : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-green-300 hover:bg-green-50/50 dark:hover:bg-green-900/10'
                                }`}
                                type="button"
                              >
                                <FiCheckCircle className="w-6 h-6 mx-auto mb-2" />
                                <span className="text-sm font-medium">Active</span>
                              </button>
                              <button
                                onClick={() => setIsActive('0')}
                                className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                                  isActive === '0'
                                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 shadow-md'
                                    : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-red-300 hover:bg-red-50/50 dark:hover:bg-red-900/10'
                                }`}
                                type="button"
                              >
                                <FiMinusCircle className="w-6 h-6 mx-auto mb-2" />
                                <span className="text-sm font-medium">Inactive</span>
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </motion.div>
                    )}

                    {/* Based Items Tab - New smart selection logic */}
                    {activeTab === 'items' && (
                      <motion.div
                        key="items"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                      >
                        {selectedProductId && !isProductStockBased(selectedProductId) ? (
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Available Items Column */}
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                  <FiBox className="w-5 h-5 text-pink-500" />
                                  Available Based Items
                                </h4>
                                <span className="text-xs px-3 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 rounded-full">
                                  {basedItems.length} available
                                </span>
                              </div>

                              <div className="space-y-3 max-h-[500px] overflow-y-auto scrollbar-hide pr-2">
                                {basedItems.length === 0 ? (
                                  <div className="bg-white dark:bg-gray-800 rounded-xl p-12 text-center border-2 border-dashed border-gray-200 dark:border-gray-700">
                                    <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                                      <FiBox className="w-8 h-8 text-gray-400" />
                                    </div>
                                    <p className="text-gray-500 dark:text-gray-400 mb-2">No items available</p>
                                    <p className="text-sm text-gray-400">
                                      No based items found for this product
                                    </p>
                                  </div>
                                ) : (
                                  basedItems.map(item => {
                                    const currentSelection = modalType === 'edit' ? updateSelectedBasedItems : selectedBasedItems;
                                    const selectedCount = currentSelection.filter(id => id === item.id).length;
                                    const isSelected = selectedCount > 0;
                                    
                                    return (
                                      <BasedItemCard
                                        key={item.id}
                                        item={item}
                                        isSelected={isSelected}
                                        onAdd={modalType === 'edit' ? addUpdateBasedItem : addBasedItem}
                                        onRemove={modalType === 'edit' ? removeUpdateBasedItem : removeBasedItem}
                                        selectionCount={selectedCount}
                                      />
                                    );
                                  })
                                )}
                              </div>
                            </div>

                            {/* Selected Items Column */}
                            <div className="space-y-4">
                              <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <FiCheckCircle className="w-5 h-5 text-pink-500" />
                                Your Selection
                                <span className="text-xs px-3 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 rounded-full ml-2">
                                  {modalType === 'edit' ? updateSelectedBasedItems.length : selectedBasedItems.length} items
                                </span>
                              </h4>

                              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-pink-100 dark:border-pink-900/30 max-h-[500px] overflow-y-auto scrollbar-hide">
                                <SelectedItemsList
                                  items={modalType === 'edit' ? updateSelectedBasedItems : selectedBasedItems}
                                  onRemove={modalType === 'edit' ? removeUpdateBasedItem : removeBasedItem}
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center py-16"
                          >
                            <div className="w-24 h-24 mx-auto bg-amber-100 dark:bg-amber-900/30 rounded-2xl flex items-center justify-center mb-4">
                              <FiInfo className="w-12 h-12 text-amber-600 dark:text-amber-400" />
                            </div>
                            <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                              Stock-Based Product
                            </h4>
                            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                              This product doesn't require based items. You can skip to pricing.
                            </p>
                          </motion.div>
                        )}
                      </motion.div>
                    )}

                    {/* Pricing Tab */}
                    {activeTab === 'pricing' && (
                      <motion.div
                        key="pricing"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-6"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Reseller Price */}
                          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-gray-100 dark:border-gray-700">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                              Reseller Price (Ks)
                            </label>
                            <div className={getInputShellClasses()}>
                              <span className="shrink-0 rounded-lg bg-pink-50 px-3 py-2 text-xs font-semibold text-pink-600 dark:bg-pink-900/30 dark:text-pink-300 sm:text-sm">
                                Ks
                              </span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.reseller_price}
                                onChange={(e) => setFormData({ ...formData, reseller_price: e.target.value })}
                                className={inputElementClasses}
                                placeholder="0"
                              />
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                              Price for reseller role
                            </p>
                          </div>

                          {/* User Price */}
                          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-gray-100 dark:border-gray-700">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                              User Price (Ks)
                            </label>
                            <div className={getInputShellClasses()}>
                              <span className="shrink-0 rounded-lg bg-pink-50 px-3 py-2 text-xs font-semibold text-pink-600 dark:bg-pink-900/30 dark:text-pink-300 sm:text-sm">
                                Ks
                              </span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.user_price}
                                onChange={(e) => setFormData({ ...formData, user_price: e.target.value })}
                                className={inputElementClasses}
                                placeholder="0"
                              />
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                              Price for regular user role
                            </p>
                          </div>
                        </div>

                        {/* Calculated Coin Price */}
                        <motion.div
                          initial={{ scale: 0.95 }}
                          animate={{ scale: 1 }}
                          className={`bg-gradient-to-r ${theme.gradient} rounded-2xl p-6 mt-4 shadow-xl`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-pink-100 mb-2 flex items-center gap-2">
                                <FiZap className="w-4 h-4" />
                                Calculated Coin Price
                              </p>
                              <p className="text-4xl font-bold text-white">
                                {modalType === 'edit' 
                                  ? calculateCoinSum(updateSelectedBasedItems) || '0.00'
                                  : calculateCoinSum(selectedBasedItems) || '0.00'} Coin
                              </p>
                              <p className="text-xs text-pink-200 mt-3 flex items-center gap-1">
                                <FiInfo className="w-3 h-3" />
                                Auto-calculated from selected based items
                              </p>
                            </div>
                            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                              <FiDollarSign className="w-8 h-8 text-white" />
                            </div>
                          </div>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Modal Footer */}
                <div className="border-t-2 border-gray-100 dark:border-gray-700 px-6 py-4 bg-white dark:bg-gray-800">
                  <div className="flex flex-col sm:flex-row justify-between gap-3">
                    {/* Left side buttons */}
                    <div className="flex gap-2 order-2 sm:order-1">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowModal(false)}
                        className="px-4 sm:px-6 py-3 rounded-xl font-medium text-sm border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex-1 sm:flex-none"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </motion.button>
                      {activeTab !== 'basic' && (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            const prevTab = activeTab === 'pricing' ? 'items' : 'basic';
                            setActiveTab(prevTab);
                          }}
                          className="px-4 sm:px-6 py-3 rounded-xl font-medium text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center gap-2 flex-1 sm:flex-none"
                        >
                          <FiChevronLeft className="w-4 h-4" />
                          <span className="hidden sm:inline">Back</span>
                        </motion.button>
                      )}
                    </div>
                    
                    {/* Right side buttons */}
                    <div className="flex gap-2 order-1 sm:order-2">
                      {activeTab !== 'pricing' ? (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            const nextTab = activeTab === 'basic' ? 'items' : 'pricing';
                            if (validateTab(activeTab)) {
                              setActiveTab(nextTab);
                            } else {
                              triggerTabShake(activeTab);
                            }
                          }}
                          className={`px-4 sm:px-8 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl font-medium text-sm hover:from-pink-700 hover:to-rose-700 transition-all shadow-lg shadow-pink-500/20 flex items-center gap-2 flex-1 sm:flex-none`}
                        >
                          <span>Next</span>
                          <FiChevronRight className="w-4 h-4" />
                        </motion.button>
                      ) : (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={modalType === 'create' ? handleCreate : handleUpdate}
                          disabled={isSubmitting}
                          className={`px-4 sm:px-10 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl font-medium text-sm hover:from-pink-700 hover:to-rose-700 transition-all shadow-lg shadow-pink-500/20 flex items-center gap-2 flex-1 sm:flex-none disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {isSubmitting ? (
                            <>
                              <FiRefreshCw className="w-4 h-4 animate-spin" />
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              <FiSave className="w-4 h-4" />
                              <span>{modalType === 'create' ? 'Create' : 'Update'}</span>
                            </>
                          )}
                        </motion.button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBulkPriceModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={resetBulkPriceTools}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              className="fixed inset-0 z-[60] overflow-hidden p-3 sm:flex sm:items-center sm:justify-center sm:p-4"
            >
              <div className="flex min-h-full items-center justify-center py-4 sm:min-h-0 sm:py-0" onClick={(e) => e.stopPropagation()}>
                <div className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-3xl flex-col overflow-hidden rounded-[28px] border border-pink-100 bg-white shadow-2xl dark:border-pink-900/30 dark:bg-gray-800 sm:max-h-[min(92vh,920px)]">
                  <div className={`shrink-0 bg-gradient-to-r ${theme.gradient} px-4 py-4 sm:px-6 sm:py-5`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center">
                        <FiTag className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Bulk Update Price</h3>
                        <p className="text-sm text-pink-100">Update multiple package prices in one place.</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={resetBulkPriceTools}
                      className="p-2 rounded-xl hover:bg-white/15 transition-colors"
                    >
                      <FiX className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>

                  <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-hide bg-gray-50 dark:bg-gray-900">
                    <div className="space-y-5 p-4 pb-28 sm:p-6 sm:pb-32">
                      <div className="rounded-2xl border border-pink-100 dark:border-pink-900/30 bg-white dark:bg-gray-800 p-4">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">အသုံးပြုပုံ</p>
                    <div className="mt-3 space-y-2 text-xs text-gray-600 dark:text-gray-300">
                      <p>1. `Load Current Prices` ကိုနှိပ်ပြီး လက်ရှိ package list ကိုယူပါ။</p>
                      <p>2. လိုတဲ့ line တွေကိုပဲ ပြင်နိုင်ပါတယ်။ format က `item_code,reseller_price,user_price` ပါ။</p>
                      <p>3. ပြင်ပြီးရင် `Apply Bulk Update` နှိပ်လိုက်တာနဲ့ အကုန်တန်း update ဖြစ်ပါမယ်။</p>
                    </div>
                  </div>

                      <div className="flex flex-col gap-3 sm:flex-row">
                        <button
                          type="button"
                          onClick={handleFetchBulkPriceTemplate}
                          disabled={bulkPriceLoading}
                          className={`px-4 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 ${theme.text} bg-pink-50 dark:bg-pink-900/20 hover:bg-pink-100 dark:hover:bg-pink-900/30 transition-colors disabled:opacity-60`}
                        >
                          {bulkPriceLoading ? (
                            <FiRefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <FiDownload className="w-4 h-4" />
                          )}
                          <span>Load Current Prices</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setBulkPriceText('')}
                          className="px-4 py-3 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          Clear
                        </button>
                      </div>

                      <div className="px-1">
                        <p className="text-xs font-medium tracking-wide text-gray-500 dark:text-gray-400">
                          item_code, reseller_price, user_price
                        </p>
                      </div>
                      <textarea
                        ref={bulkPriceTextareaRef}
                        aria-label="Bulk update text"
                        value={bulkPriceText}
                        onChange={(e) => setBulkPriceText(e.target.value)}
                        placeholder={'item_code,reseller_price,user_price\npack10,12000,13500'}
                        className="w-full min-h-[320px] overflow-hidden resize-none rounded-2xl border-2 border-pink-100 dark:border-pink-900/30 bg-white dark:bg-gray-800 px-4 py-3 pb-24 text-sm text-gray-900 dark:text-white outline-none focus:border-pink-400 sm:min-h-[420px]"
                      />
                    </div>
                  </div>

                  <div className="shrink-0 border-t border-pink-100/80 bg-white/95 p-4 backdrop-blur dark:border-pink-900/30 dark:bg-gray-800/95 sm:p-6">
                    <div className="flex flex-col justify-end gap-3 sm:flex-row">
                      <button
                        type="button"
                        onClick={resetBulkPriceTools}
                        className="px-5 py-3 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmitBulkPriceUpdate}
                        disabled={bulkPriceSubmitting || bulkPriceLoading}
                        className={`px-5 py-3 rounded-xl text-sm font-semibold bg-gradient-to-r ${theme.primary} text-white hover:from-pink-700 hover:to-rose-700 transition-all shadow-lg shadow-pink-500/20 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                      >
                        {bulkPriceSubmitting ? (
                          <>
                            <FiRefreshCw className="w-4 h-4 animate-spin" />
                            <span>Updating...</span>
                          </>
                        ) : (
                          <>
                            <FiSave className="w-4 h-4" />
                            <span>Apply Bulk Update</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showLimitSeriesPromptModal && pendingLimitSeriesItem && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70]"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              className="fixed inset-0 z-[80] overflow-y-auto p-3 sm:flex sm:items-center sm:justify-center sm:p-4"
            >
              <div className="flex min-h-full items-center justify-center py-4 sm:min-h-0 sm:py-0">
                <div className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-lg flex-col overflow-hidden rounded-[28px] border border-pink-100 bg-white shadow-2xl dark:border-pink-900/30 dark:bg-gray-800 sm:max-h-[min(88vh,720px)]">
                  <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                      <FiLayers className="w-6 h-6 text-pink-600 dark:text-pink-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Open Limit Series Create?</h3>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        ဒီ <span className="font-medium text-gray-900 dark:text-white">{pendingLimitSeriesItem.code}</span> အတွက် တစ်ခုချင်းစီထည့်စရာမလိုပဲ
                        သူ့မှာရှိတဲ့ limit count အတိုင်း {pendingLimitSeriesItem.code}1, {pendingLimitSeriesItem.code}2, ... အစရှိသဖြင့်
                        Auto ထည့်သွင်းသွားနိုင်ပါသည်။
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 rounded-2xl border border-pink-100 dark:border-pink-900/30 bg-pink-50 dark:bg-pink-900/20 p-4 text-sm text-pink-700 dark:text-pink-300">
                    <p>{pendingLimitSeriesItem.code} • limit {pendingLimitSeriesItem.limit_count}</p>
                  </div>

                  </div>

                  <div className="shrink-0 border-t border-pink-100/80 bg-white/95 p-4 backdrop-blur dark:border-pink-900/30 dark:bg-gray-800/95 sm:p-6">
                    <div className="flex flex-col justify-end gap-3 sm:flex-row">
                      <button
                        type="button"
                        onClick={() => {
                          setShowLimitSeriesPromptModal(false);
                          setPendingLimitSeriesItem(null);
                        }}
                        className="px-5 py-3 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        Keep Selecting
                      </button>
                      <button
                        type="button"
                        onClick={() => openLimitSeriesModalForItem(pendingLimitSeriesItem)}
                        className={`px-5 py-3 rounded-xl text-sm font-semibold bg-gradient-to-r ${theme.primary} text-white hover:from-pink-700 hover:to-rose-700 transition-all shadow-lg shadow-pink-500/20 flex items-center justify-center gap-2`}
                      >
                        <FiLayers className="w-4 h-4" />
                        <span>Open Limit Series</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showLimitSeriesModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80]"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              className="fixed inset-0 z-[90] overflow-y-auto p-3 sm:flex sm:items-center sm:justify-center sm:p-4"
            >
              <div className="flex min-h-full items-center justify-center py-4 sm:min-h-0 sm:py-0">
                <div className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-3xl flex-col overflow-hidden rounded-[28px] border border-pink-100 bg-white shadow-2xl dark:border-pink-900/30 dark:bg-gray-800 sm:max-h-[min(90vh,920px)]">
                  <div className={`shrink-0 bg-gradient-to-r ${theme.gradient} px-4 py-4 sm:px-6 sm:py-5`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center">
                        <FiLayers className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Limit Series Create</h3>
                        <p className="text-sm text-pink-100">Create the full package series for the selected limited based item.</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={resetLimitSeriesTools}
                      className="p-2 rounded-xl hover:bg-white/15 transition-colors"
                    >
                      <FiX className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>

                  <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                    <div className="space-y-5 p-4 sm:p-6">
                      {selectedLimitSeriesItem && (
                        <div className="rounded-2xl border border-pink-100 dark:border-pink-900/30 bg-white dark:bg-gray-800 p-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{selectedLimitSeriesItem.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{selectedLimitSeriesItem.code} • limit {selectedLimitSeriesItem.limit_count}</p>
                        </div>
                      </div>
                    </div>
                  )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Base Code</label>
                      <input
                        type="text"
                        value={limitSeriesForm.code}
                        onChange={(e) => setLimitSeriesForm(prev => ({ ...prev, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))}
                        placeholder="wp"
                        className={`w-full px-4 py-3 rounded-xl border bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white outline-none focus:border-pink-400 ${limitSeriesCodeError ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'}`}
                      />
                      {limitSeriesCodeError ? (
                        <p className="mt-2 text-xs text-red-500 dark:text-red-400">{limitSeriesCodeError}</p>
                      ) : (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Result pattern: `code`, `code1`, `code2`...</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Base Name</label>
                      <input
                        type="text"
                        value={limitSeriesForm.name}
                        onChange={(e) => setLimitSeriesForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Weekly Pass"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white outline-none focus:border-pink-400"
                      />
                    </div>
                  </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Reseller Price</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={limitSeriesForm.reseller_price}
                        onChange={(e) => setLimitSeriesForm(prev => ({ ...prev, reseller_price: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white outline-none focus:border-pink-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">User Price</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={limitSeriesForm.user_price}
                        onChange={(e) => setLimitSeriesForm(prev => ({ ...prev, user_price: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white outline-none focus:border-pink-400"
                      />
                    </div>
                  </div>

                      <div className="rounded-2xl border border-pink-100 dark:border-pink-900/30 bg-white dark:bg-gray-800 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Preview</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Review the package codes and names before creating the series.</p>
                      </div>
                      <span className="px-3 py-1 rounded-full bg-pink-50 dark:bg-pink-900/20 text-xs font-medium text-pink-600 dark:text-pink-300">
                        {limitSeriesPreview.length} items
                      </span>
                    </div>

                    {limitSeriesPreview.length > 0 ? (
                      <div className="mt-4 max-h-48 overflow-y-auto scrollbar-hide space-y-2 pr-1">
                        {limitSeriesPreview.map(item => (
                          <div key={item.code} className="flex items-center justify-between gap-3 rounded-xl border border-pink-100 dark:border-pink-900/30 px-3 py-2">
                            <div className="min-w-0">
                              <p className="break-words text-sm font-medium text-gray-900 dark:text-white">{item.name}</p>
                              <p className="break-all text-xs text-gray-500 dark:text-gray-400">{item.code}</p>
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="text-xs font-medium text-amber-600 dark:text-amber-300">R: {item.reseller_price} Ks</p>
                              <p className="text-xs font-medium text-blue-600 dark:text-blue-300">U: {item.user_price} Ks</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">Add a valid base code and base name to generate the preview list.</p>
                    )}
                  </div>

                    </div>
                  </div>

                  <div className="shrink-0 border-t border-pink-100/80 bg-white/95 p-4 backdrop-blur dark:border-pink-900/30 dark:bg-gray-800/95 sm:p-6">
                    <div className="flex flex-col justify-end gap-3 sm:flex-row">
                      <button
                        type="button"
                        onClick={resetLimitSeriesTools}
                        className="px-5 py-3 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleLimitSeriesCreate}
                        disabled={limitSeriesSubmitting || Boolean(limitSeriesCodeError)}
                        className={`px-5 py-3 rounded-xl text-sm font-semibold bg-gradient-to-r ${theme.primary} text-white hover:from-pink-700 hover:to-rose-700 transition-all shadow-lg shadow-pink-500/20 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                      >
                        {limitSeriesSubmitting ? (
                          <>
                            <FiRefreshCw className="w-4 h-4 animate-spin" />
                            <span>Creating...</span>
                          </>
                        ) : (
                          <>
                            <FiLayers className="w-4 h-4" />
                            <span>Create Limit Series</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showLimitSeriesResultModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeLimitSeriesResultModal}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[90]"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            >
              <div className="w-full max-w-2xl rounded-3xl bg-white dark:bg-gray-800 shadow-2xl overflow-hidden border border-pink-100 dark:border-pink-900/30">
                <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-5`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center">
                        <FiCheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Limit Series Result</h3>
                        <p className="text-sm text-pink-100">
                          Created: {limitSeriesResult?.created_count || 0} • Skipped: {limitSeriesResult?.skipped_count || 0}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={closeLimitSeriesResultModal}
                      className="p-2 rounded-xl hover:bg-white/15 transition-colors"
                    >
                      <FiX className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>

                <div className="p-6 bg-gray-50 dark:bg-gray-900 space-y-5 max-h-[80vh] overflow-y-auto">
                  <div className="rounded-2xl border border-pink-100 dark:border-pink-900/30 bg-white dark:bg-gray-800 p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {limitSeriesResult?.created_count > 0
                        ? 'These packages were created successfully.'
                        : 'No new packages were created in this run.'}
                    </p>
                    {limitSeriesResult?.skipped_count > 0 && (
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                        Some codes were skipped because they already exist.
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-green-100 dark:border-green-900/30 bg-white dark:bg-gray-800 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Created Items</p>
                        <span className="px-3 py-1 rounded-full bg-green-50 dark:bg-green-900/20 text-xs font-medium text-green-600 dark:text-green-300">
                          {limitSeriesResult?.created_count || 0}
                        </span>
                      </div>
                      {Array.isArray(limitSeriesResult?.created_codes) && limitSeriesResult.created_codes.length > 0 ? (
                        <div className="mt-4 max-h-60 overflow-y-auto scrollbar-hide space-y-2 pr-1">
                          {limitSeriesResult.created_codes.map(code => (
                            <div key={code} className="rounded-xl border border-green-100 dark:border-green-900/30 px-3 py-2 text-sm text-green-700 dark:text-green-300">
                              {code}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">No items were created.</p>
                      )}
                    </div>

                    <div className="rounded-2xl border border-amber-100 dark:border-amber-900/30 bg-white dark:bg-gray-800 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Skipped Items</p>
                        <span className="px-3 py-1 rounded-full bg-amber-50 dark:bg-amber-900/20 text-xs font-medium text-amber-600 dark:text-amber-300">
                          {limitSeriesResult?.skipped_count || 0}
                        </span>
                      </div>
                      {Array.isArray(limitSeriesResult?.skipped_codes) && limitSeriesResult.skipped_codes.length > 0 ? (
                        <div className="mt-4 max-h-60 overflow-y-auto scrollbar-hide space-y-2 pr-1">
                          {limitSeriesResult.skipped_codes.map(code => (
                            <div key={code} className="rounded-xl border border-amber-100 dark:border-amber-900/30 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
                              {code}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">No items were skipped.</p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={closeLimitSeriesResultModal}
                      className={`px-5 py-3 rounded-xl text-sm font-semibold bg-gradient-to-r ${theme.primary} text-white hover:from-pink-700 hover:to-rose-700 transition-all shadow-lg shadow-pink-500/20`}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDeleteModal(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                      <FiAlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Item</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this item?
                      </p>
                    </div>
                  </div>
                  
                  {itemToDelete && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-6">
                      <p className="font-medium text-gray-900 dark:text-white">{itemToDelete.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Code: {itemToDelete.code}</p>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <FiTrash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
};

export default CustomProductItems;
